import { useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Avatar } from '@/design/components'
import type { NavItem } from '@/lib/types'
import { useAuth } from './AuthProvider'
import { useMe } from './MeContextProvider'
import { Logo } from './Logo'

/** Kolejność grup jak w mockupach (CONTRACTS.md §7); reszta grup — na końcu. */
const GROUP_ORDER = ['PRZEGLĄD', 'OBIEG DOKUMENTÓW', 'SPRAWY', 'ADMINISTRACJA', 'PODGLĄD']

export const DASHBOARD_NAV: NavItem = { group: 'PRZEGLĄD', label: 'Pulpit', route: '/' }

interface NavGroup {
  name: string
  items: NavItem[]
}

function groupNav(items: NavItem[]): NavGroup[] {
  const groups = new Map<string, NavItem[]>()
  for (const item of items) {
    const key = (item.group ?? '').toUpperCase() || 'INNE'
    const bucket = groups.get(key)
    if (bucket) bucket.push(item)
    else groups.set(key, [item])
  }
  return [...groups.entries()]
    .map(([name, groupItems]) => ({ name, items: groupItems }))
    .sort((a, b) => {
      const ai = GROUP_ORDER.indexOf(a.name)
      const bi = GROUP_ORDER.indexOf(b.name)
      return (ai === -1 ? GROUP_ORDER.length : ai) - (bi === -1 ? GROUP_ORDER.length : bi)
    })
}

export interface SidebarProps {
  /** Zamyka szufladę po kliknięciu pozycji (widok < 900px). */
  onNavigate?: () => void
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const { firmName, navItems, user } = useMe()
  const { logout } = useAuth()

  const groups = useMemo(() => {
    const items: NavItem[] = navItems.map(({ group, label, route, badge_endpoint }) => ({
      group,
      label,
      route,
      badge_endpoint,
    }))
    // Pulpit należy do rdzenia frontendu — moduły go nie deklarują.
    const hasDashboard = items.some((item) => item.route === '/')
    return groupNav(hasDashboard ? items : [DASHBOARD_NAV, ...items])
  }, [navItems])

  return (
    <aside className="flex h-full w-sidebar shrink-0 flex-col bg-navy-800 text-sidebar-item">
      <div className="border-b border-white/[0.07] px-[18px] py-[18px]">
        <Logo firmName={firmName} />
      </div>

      <nav className="am-scroll flex-1 overflow-y-auto py-3">
        {groups.map((group) => (
          <div key={group.name} className="mb-1.5">
            <p className="px-[18px] pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.11em] text-sidebar-muted">
              {group.name}
            </p>
            {group.items.map((item) => (
              <SidebarLink key={`${item.route}-${item.label}`} item={item} onNavigate={onNavigate} />
            ))}
          </div>
        ))}
      </nav>

      <div className="flex items-center gap-2.5 border-t border-white/[0.07] px-[18px] py-3.5">
        <Avatar name={user.full_name} size="lg" tone="light" className="!bg-navy-600 !text-white" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-medium leading-tight text-white">{user.full_name}</p>
          <p className="truncate text-[12px] leading-tight text-sidebar-muted">
            {user.title ?? user.roles?.[0] ?? ''}
          </p>
        </div>
        <button
          type="button"
          title="Wyloguj"
          aria-label="Wyloguj"
          onClick={() => void logout()}
          className="shrink-0 rounded p-1.5 text-sidebar-muted transition-colors duration-fast hover:bg-white/[0.08] hover:text-white"
        >
          <svg viewBox="0 0 16 16" aria-hidden className="h-[15px] w-[15px]">
            <path
              d="M6 2H3.5A1.5 1.5 0 0 0 2 3.5v9A1.5 1.5 0 0 0 3.5 14H6M10.5 11 14 8l-3.5-3M13.5 8H6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </aside>
  )
}

function SidebarLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.route}
      end={item.route === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'relative flex h-[34px] items-center gap-2 pl-[22px] pr-3 text-base transition-colors duration-fast',
          isActive
            ? 'bg-navy-700 font-semibold text-white before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:bg-gold before:content-[""]'
            : 'text-sidebar-item hover:bg-white/[0.05] hover:text-white',
        )
      }
    >
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.badge_endpoint ? <NavBadge endpoint={item.badge_endpoint} /> : null}
    </NavLink>
  )
}

/** Odczytuje licznik z `badge_endpoint` modułu (CONTRACTS.md §4). */
function NavBadge({ endpoint }: { endpoint: string }) {
  const { data } = useQuery({
    queryKey: ['nav-badge', endpoint],
    queryFn: () => api.get<unknown>(endpoint),
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: false,
  })

  const count = readCount(data)
  if (!count) return null

  return (
    <span className="ml-auto inline-flex h-[19px] min-w-[21px] items-center justify-center rounded-[2px] bg-gold px-1.5 text-[11.5px] font-semibold leading-none text-navy-900">
      {count}
    </span>
  )
}

/** Akceptuje `5`, `{count}`, `{value}`, `{total}` — kontrakt nie ustala kształtu. */
function readCount(payload: unknown): number | null {
  if (typeof payload === 'number') return payload || null
  if (payload && typeof payload === 'object') {
    for (const key of ['count', 'value', 'total', 'badge']) {
      const value = (payload as Record<string, unknown>)[key]
      if (typeof value === 'number') return value || null
    }
  }
  return null
}
